// FieldVision - DownloadModal.tsx
// Modal til valg af terminal og download af tildelingskort
// FIX 2026-01-04: POST request med zones i body saa opdaterede rates bruges ved download
// NEW 2025-12-31: Trimble terminal support tilfojet

import React, { useState } from 'react';
import { ZoneData, ZONE_COLORS } from './ZoneRow';

interface Terminal {
  id: string;
  name: string;
  enabled: boolean;
  icon: string;
}

const TERMINALS: Terminal[] = [
  { id: 'john-deere', name: 'John Deere', enabled: true, icon: 'JD' },
  { id: 'claas', name: 'CLAAS', enabled: false, icon: 'CL' },
  { id: 'fendt', name: 'Fendt', enabled: false, icon: 'FE' },
  { id: 'amazone', name: 'Amazone', enabled: true, icon: 'AZ' },
  { id: 'trimble', name: 'Trimble', enabled: true, icon: 'TR' },
  { id: 'muller', name: 'Müller Elektronik', enabled: true, icon: 'MU' },
  { id: 'kverneland', name: 'Kverneland', enabled: true, icon: 'KV' },
  { id: 'cnh', name: 'CNH (Case/New Holland)', enabled: true, icon: 'CH' },
  { id: 'isoxml', name: 'ISO-XML', enabled: false, icon: 'ISO' },
];

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  mapId: number;
  source?: string;      // Kortets kilde (drone/satellite)
  mapFormat?: string;   // Terminal format kortet er uploadet som (drone-kort)
  zones: ZoneData[];
  unit: 'l/ha' | 'kg/ha';
  totals: { hectares: number; total: number };
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  isOpen,
  onClose,
  mapId,
  source,
  mapFormat,
  zones,
  unit,
  totals,
}) => {
  const [selectedTerminal, setSelectedTerminal] = useState<string>('john-deere');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Beregn hvilke terminaler der er tilgængelige
  const availableTerminals = React.useMemo(() => {
    // Satellit-kort: alle aktiverede terminaler
    if (source === 'satellite' || source === 'satellite-microservice') {
      return TERMINALS;
    }

    // Drone-kort: kun den terminal kortet er uploadet som
    if (mapFormat) {
      return TERMINALS.map(t => ({
        ...t,
        enabled: t.id === mapFormat
      }));
    }

    // Fallback: alle aktiverede terminaler
    return TERMINALS;
  }, [source, mapFormat]);

  // Sæt default terminal til mapFormat hvis det er drone-kort
  React.useEffect(() => {
    if (source !== 'satellite' && source !== 'satellite-microservice' && mapFormat) {
      setSelectedTerminal(mapFormat);
    }
  }, [source, mapFormat]);

  if (!isOpen) return null;

  const activeZones = zones.filter(z => z.zone !== 0).sort((a, b) => a.zone - b.zone);
  const unitLabel = unit === 'l/ha' ? 'l' : 'kg';

  const handleDownload = async () => {
    const terminal = availableTerminals.find(t => t.id === selectedTerminal);
    if (!terminal?.enabled) {
      setError('Denne terminal er ikke tilgaengelig endnu');
      return;
    }

    try {
      setDownloading(true);
      setError(null);

      // Hent token fra localStorage
      const token = localStorage.getItem('token');

      // FIX 2026-01-04: POST request med zones i body saa opdaterede rates bruges
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/zonemaps/${mapId}/download-file?terminal=${selectedTerminal}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            zones: zones,
            unit: unit
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Download fejlede');
      }

      // Hent filename fra Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'tildelingskort.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Konverter response til blob og download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      console.error('Download error:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke downloade tildelingskort. Proev igen.');
    } finally {
      setDownloading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <h2>Download tildelingskort</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Zone opsummering */}
        <div className="zone-summary-section">
          <h3>Zone-opsummering</h3>
          <div className="zone-summary-list">
            {activeZones.map(zone => (
              <div key={zone.zone} className="zone-summary-row">
                <div
                  className="zone-color"
                  style={{ backgroundColor: ZONE_COLORS[zone.zone] || '#9ca3af' }}
                />
                <span className="zone-name">Zone {zone.zone}</span>
                <span className="zone-rate">{zone.rate} {unit}</span>
                <span className="zone-hectares">{zone.hectares.toFixed(1)} ha</span>
                <span className="zone-total">{Math.round(zone.rate * zone.hectares)} {unitLabel}</span>
              </div>
            ))}
          </div>
          <div className="zone-summary-total">
            <span>Total:</span>
            <span>{totals.hectares.toFixed(1)} ha</span>
            <span>{Math.round(totals.total).toLocaleString('da-DK')} {unitLabel}</span>
          </div>
        </div>

        {/* Terminal valg */}
        <div className="terminal-section">
          <h3>Vaelg terminal</h3>
          <div className="terminal-grid">
            {availableTerminals.map(terminal => (
              <button
                key={terminal.id}
                className={`terminal-btn ${selectedTerminal === terminal.id ? 'selected' : ''} ${!terminal.enabled ? 'disabled' : ''}`}
                onClick={() => terminal.enabled && setSelectedTerminal(terminal.id)}
                disabled={!terminal.enabled}
              >
                <span className="terminal-icon">{terminal.icon}</span>
                <span className="terminal-name">{terminal.name}</span>
                {!terminal.enabled && <span className="coming-soon">Kommer snart</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose}>
            Annuller
          </button>
          <button
            className="btn-download"
            onClick={handleDownload}
            disabled={downloading || !availableTerminals.find(t => t.id === selectedTerminal)?.enabled}
          >
            {downloading ? 'Downloader...' : 'Download'}
          </button>
        </div>
      </div>

      <style>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          color: #111827;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #6b7280;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .close-btn:hover {
          color: #374151;
        }

        .zone-summary-section {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .zone-summary-section h3 {
          margin: 0 0 12px 0;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .zone-summary-list {
          background: #f9fafb;
          border-radius: 8px;
          padding: 12px;
        }

        .zone-summary-row {
          display: grid;
          grid-template-columns: 16px 60px 1fr 60px 60px;
          gap: 8px;
          align-items: center;
          padding: 6px 0;
          font-size: 13px;
        }

        .zone-color {
          width: 16px;
          height: 16px;
          border-radius: 4px;
        }

        .zone-name {
          font-weight: 500;
          color: #374151;
        }

        .zone-rate {
          color: #6b7280;
        }

        .zone-hectares {
          text-align: right;
          color: #6b7280;
        }

        .zone-total {
          text-align: right;
          font-weight: 500;
          color: #16a34a;
        }

        .zone-summary-total {
          display: flex;
          justify-content: space-between;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          font-weight: 600;
          color: #16a34a;
        }

        .terminal-section {
          padding: 20px;
        }

        .terminal-section h3 {
          margin: 0 0 12px 0;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .terminal-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .terminal-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px 8px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.15s;
          position: relative;
        }

        .terminal-btn:hover:not(.disabled) {
          border-color: #16a34a;
        }

        .terminal-btn.selected {
          border-color: #16a34a;
          background-color: #f0fdf4;
        }

        .terminal-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .terminal-icon {
          font-size: 18px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 4px;
        }

        .terminal-name {
          font-size: 11px;
          color: #6b7280;
        }

        .coming-soon {
          position: absolute;
          bottom: 2px;
          font-size: 8px;
          color: #9ca3af;
        }

        .error-message {
          margin: 0 20px;
          padding: 12px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 6px;
          font-size: 13px;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel {
          flex: 1;
          padding: 12px 20px;
          background: white;
          color: #6b7280;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-cancel:hover {
          background: #f9fafb;
        }

        .btn-download {
          flex: 1;
          padding: 12px 20px;
          background: #16a34a;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-download:hover:not(:disabled) {
          background: #15803d;
        }

        .btn-download:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default DownloadModal;
